import { ref, watch, Component } from 'vue'

type ShowParams = {
  /**
   * turns modal style with following options:
   * - modal - (default) Empty modal skeleton with heading, body and footer slots, or title and text props
   * - alert - Modal that usually consists of an icon, message and set of actions
   * - dialog - Modal that requires some sort of user input
   * - tooltip - Modal intended for providing user with a message or instructions
   */
  preset?: 'modal' | 'alert' | 'dialog' | 'tooltip'
  // If headerComponent is not present, this can be used to provide the header content
  title?: string
  // If bodyComponent is not present, this can be used to provide the body content
  text?: string
  // If footerComponent is not present, this can be used to provide the footer content
  footer?: string
  // Vue component for header slot of the modal
  headerComponent?: Component
  // Passes the props to header component
  headerProps?: { [key: string]: unknown }
  // Applies skin to header component
  headerStyle?: 'light' | 'dark'
  // Vue component for body slot of the modal
  bodyComponent?: Component
  // Passes the props to body component
  bodyProps?: { [key: string]: unknown }
  // Vue component for footer slot of the modal
  footerComponent?: Component
  // Passes the props to footer component
  footerProps?: { [key: string]: unknown }
  showCloseButton?: boolean
  closeOnEscape?: boolean
  role?: string
  ariaLabel?: string
  // Allows the modal to be dismissed on route update
  routeDismiss?: boolean
  size?: 'small' | 'medium' | 'large'
  // (Available only for 'alert' preset): Icon for the Alert modal
  icon?: string
  // (Available only for 'alert' preset): Icon color for the Alert modal
  type?: 'info' | 'warning' | 'error' | 'success'
  textAlign?: 'left' | 'center'
  allowOverflow?: boolean
  // (Available only for presets: 'alert', 'dialog'): Lists of button actions
  actions?: {
    text: string
    event?: string
    props?: any
    listeners?: any
  }[]
}

const modal = ref<null | ShowParams>()
type useModal = {
  dismiss: (args?: unknown) => void
  modalState: typeof modal
  show: (params: ShowParams) => Promise<unknown>
}
let callbackArgs: unknown = null

export const useModal = (): useModal => {
  const show = (params: ShowParams) => {
    return new Promise((resolve) => {
      modal.value = params
      const stopWatching = watch(modal, () => {
        if (modal.value === null) {
          resolve(callbackArgs)
          stopWatching()
        }
      })
    })
  }

  const dismiss = (args?: unknown) => {
    modal.value = null
    callbackArgs = args
  }
  return {
    show,
    dismiss,
    modalState: modal,
  }
}

export default useModal
